import React, { useState, useEffect } from 'react';
import { Card, Button, Spin, Alert, Tabs, Typography, Space, Tag, Progress, Timeline, Statistic, Row, Col } from 'antd';
import { 
    BarChartOutlined, 
    ClockCircleOutlined, 
    DollarOutlined,
    HeartOutlined,
    TrophyOutlined,
    RobotOutlined,
    CalendarOutlined,
    ThunderboltOutlined,
    ShoppingCartOutlined
} from '@ant-design/icons';
import aiOrderInsights from '../../services/aiOrderInsights';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const AIOrderInsights = ({ 
    orders = [],
    visible = false, 
    onClose,
    className = ""
}) => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [insights, setInsights] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (visible && orders.length > 0) {
            loadOrderInsights();
        }
    }, [visible, orders]);

    const loadOrderInsights = async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await aiOrderInsights.getComprehensiveInsights(orders);
            
            if (result.success) {
                setInsights(result.insights);
            } else {
                setError(result.error || 'Failed to load insights');
            }
        } catch (error) {
            console.error('Order insights error:', error);
            setError('Failed to load order insights');
        } finally {
            setLoading(false);
        }
    };

    const renderOverview = () => {
        if (!insights) return <Spin />;

        const patterns = insights.patterns || {};
        const predictions = insights.predictions || {};
        const valueTrends = insights.valueTrends || {};

        return (
            <div className="space-y-6">
                <Row gutter={16}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Orders"
                                value={patterns.total_orders || 0}
                                prefix={<ShoppingCartOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Spent"
                                value={valueTrends.total_spent || 0}
                                prefix={<DollarOutlined />}
                                formatter={(value) => `₹${value}`}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Avg Order Value"
                                value={valueTrends.average_order_value || 0}
                                prefix={<DollarOutlined />}
                                formatter={(value) => `₹${Math.round(value)}`}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Favorite Cuisine"
                                value={patterns.most_ordered_cuisine || 'N/A'}
                                prefix={<TrophyOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>

                <Card title="Order Patterns">
                    <div className="space-y-4">
                        <div>
                            <Text strong>Peak Ordering Time: </Text>
                            <Tag color="blue">{patterns.peak_ordering_time || 'Evening'}</Tag>
                        </div>
                        <div>
                            <Text strong>Order Frequency: </Text>
                            <Tag color="green">{patterns.order_frequency || 'Weekly'}</Tag>
                        </div>
                        <div>
                            <Text strong>Next Order Prediction: </Text>
                            <Tag color="orange">{predictions.next_order_timing || 'Within 3-5 days'}</Tag>
                        </div>
                    </div>
                </Card>

                <Card title="Quick Insights">
                    <Timeline>
                        <Timeline.Item color="green">
                            <Text>Most active ordering period: {patterns.peak_ordering_time || 'Evening'}</Text>
                        </Timeline.Item>
                        <Timeline.Item color="blue">
                            <Text>Preferred cuisine: {patterns.most_ordered_cuisine || 'Indian'}</Text>
                        </Timeline.Item>
                        <Timeline.Item color="orange">
                            <Text>Next order likely: {predictions.next_order_timing || 'Within 3-5 days'}</Text>
                        </Timeline.Item>
                        <Timeline.Item color="purple">
                            <Text>Average order value: ₹{Math.round(valueTrends.average_order_value || 0)}</Text>
                        </Timeline.Item>
                    </Timeline>
                </Card>
            </div>
        );
    };

    const renderPredictions = () => {
        if (!insights) return <Spin />;

        const predictions = insights.predictions || {};

        return (
            <div className="space-y-4">
                <Card title="Next Order Prediction">
                    <div className="space-y-3">
                        <div>
                            <Text strong>Timing: </Text>
                            <Tag color="blue">{predictions.next_order_timing || 'Within 3-5 days'}</Tag>
                        </div>
                        <div>
                            <Text strong>Confidence: </Text>
                            <Progress 
                                percent={predictions.confidence === 'High' ? 80 : predictions.confidence === 'Medium' ? 60 : 40} 
                                format={() => predictions.confidence || 'Medium'}
                            />
                        </div>
                    </div>
                </Card>

                <Card title="Recommended Dishes">
                    <div className="space-y-2">
                        {(predictions.recommended_dishes || []).map((dish, index) => (
                            <Tag key={index} color="green">{dish}</Tag>
                        ))}
                    </div>
                </Card>

                <Card title="Suggested Vendors">
                    <div className="space-y-2">
                        {(predictions.suggested_vendors || []).map((vendor, index) => (
                            <Tag key={index} color="blue">{vendor}</Tag>
                        ))}
                    </div>
                </Card>

                <Card title="Optimal Order Time">
                    <div className="flex items-center gap-2">
                        <ClockCircleOutlined className="text-orange-500" />
                        <Text>{predictions.optimal_order_time || 'Evening'}</Text>
                    </div>
                </Card>
            </div>
        );
    };

    const renderRecommendations = () => {
        if (!insights) return <Spin />;

        const recommendations = insights.recommendations || {};

        return (
            <div className="space-y-4">
                <Card title="Dish Recommendations">
                    <div className="space-y-2">
                        {(recommendations.dishes || []).map((dish, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <ThunderboltOutlined className="text-purple-500" />
                                <Text>{dish}</Text>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card title="Vendor Suggestions">
                    <div className="space-y-2">
                        {(recommendations.vendors || []).map((vendor, index) => (
                            <Tag key={index} color="blue">{vendor}</Tag>
                        ))}
                    </div>
                </Card>

                <Card title="New Cuisines to Try">
                    <div className="space-y-2">
                        {(recommendations.new_cuisines || []).map((cuisine, index) => (
                            <Tag key={index} color="orange">{cuisine}</Tag>
                        ))}
                    </div>
                </Card>

                <Card title="Seasonal Recommendations">
                    <Paragraph>{recommendations.seasonal_recommendations || 'No seasonal recommendations available'}</Paragraph>
                </Card>

                <Card title="Budget-Friendly Options">
                    <div className="space-y-2">
                        {(recommendations.budget_options || []).map((option, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <DollarOutlined className="text-green-500" />
                                <Text>{option}</Text>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        );
    };

    const renderHealthInsights = () => {
        if (!insights) return <Spin />;

        const healthInsights = insights.healthInsights || {};

        return (
            <div className="space-y-4">
                <Card title="Nutritional Balance">
                    <div className="space-y-3">
                        <div>
                            <Text strong>Overall Balance: </Text>
                            <Tag color={healthInsights.nutritional_balance === 'Good' ? 'green' : 'orange'}>
                                {healthInsights.nutritional_balance || 'Moderate'}
                            </Tag>
                        </div>
                        <div>
                            <Text strong>Dietary Diversity: </Text>
                            <Progress 
                                percent={healthInsights.dietary_diversity === 'High' ? 80 : healthInsights.dietary_diversity === 'Moderate' ? 60 : 40}
                                format={() => healthInsights.dietary_diversity || 'Moderate'}
                            />
                        </div>
                    </div>
                </Card>

                <Card title="Health Recommendations">
                    <div className="space-y-2">
                        {(healthInsights.recommendations || []).map((rec, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <HeartOutlined className="text-red-500" />
                                <Text>{rec}</Text>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card title="Healthy Alternatives">
                    <div className="space-y-2">
                        {(healthInsights.healthy_alternatives || []).map((alt, index) => (
                            <Tag key={index} color="green">{alt}</Tag>
                        ))}
                    </div>
                </Card>
            </div>
        );
    };

    const renderOptimization = () => {
        if (!insights) return <Spin />;

        const optimization = insights.optimization || {};

        return (
            <div className="space-y-4">
                <Card title="Cost Optimization">
                    <div className="space-y-2">
                        {(optimization.cost_savings || []).map((tip, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <DollarOutlined className="text-green-500" />
                                <Text>{tip}</Text>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card title="Time-Saving Tips">
                    <div className="space-y-2">
                        {(optimization.time_saving || []).map((tip, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <ClockCircleOutlined className="text-blue-500" />
                                <Text>{tip}</Text>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card title="Bulk Ordering Opportunities">
                    <div className="space-y-2">
                        {(optimization.bulk_ordering || []).map((tip, index) => (
                            <Tag key={index} color="purple">{tip}</Tag>
                        ))}
                    </div>
                </Card>

                <Card title="Loyalty Tips">
                    <div className="space-y-2">
                        {(optimization.loyalty_tips || []).map((tip, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <TrophyOutlined className="text-yellow-500" />
                                <Text>{tip}</Text>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        );
    };

    if (!visible) return null;

    return (
        <Card
            className={`w-full max-w-6xl ${className}`}
            title={
                <div className="flex items-center gap-2">
                    <RobotOutlined className="text-blue-500" />
                    <span>AI Order Insights</span>
                    <Text className="text-gray-500">- {orders.length} orders analyzed</Text>
                </div>
            }
            extra={
                onClose && (
                    <Button type="text" onClick={onClose}>
                        Close
                    </Button>
                )
            }
        >
            {error && (
                <Alert
                    message={error}
                    type="error"
                    showIcon
                    className="mb-4"
                />
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Overview" key="overview">
                    {loading ? <Spin /> : renderOverview()}
                </TabPane>
                <TabPane tab="Predictions" key="predictions">
                    {loading ? <Spin /> : renderPredictions()}
                </TabPane>
                <TabPane tab="Recommendations" key="recommendations">
                    {loading ? <Spin /> : renderRecommendations()}
                </TabPane>
                <TabPane tab="Health Insights" key="health">
                    {loading ? <Spin /> : renderHealthInsights()}
                </TabPane>
                <TabPane tab="Optimization" key="optimization">
                    {loading ? <Spin /> : renderOptimization()}
                </TabPane>
            </Tabs>
        </Card>
    );
};

export default AIOrderInsights;
